import { AssociationImageRequest } from "../types/association.types";
import {
  ALLOWED_IMAGE_TYPES,
  PendingMediaFile,
  StoredMediaFile,
  buildImagesUpdate,
  validateMediaFile,
} from "./mediaPayload";

// The association's media write with its logo (apoteka-back @ cbd77705). The
// response serves the logo on its own (`logo`) and keeps it out of `images`,
// but the write still takes ONE row set: `updateImages: true` replaces every
// stored row, the logo included, and `isSmall` decides what each row becomes
// (`1` the logo, `0` a banner). So the stored logo row is always sent back,
// otherwise a media save deletes it.

/** The logo in the editor: the stored one (kept by location), a picked file, or none. */
export type LogoSlot =
  | { kind: "stored"; file: StoredMediaFile }
  | { kind: "pending"; file: File }
  | null;

export const LOGO_TYPE_ERROR = "Logo mora biti slika (JPEG, PNG ili WebP).";

/** A logo is an image, under the same type whitelist and size limit as any image. */
export const validateLogoFile = (file: File): string | null =>
  ALLOWED_IMAGE_TYPES.includes(file.type)
    ? validateMediaFile(file)
    : LOGO_TYPE_ERROR;

export interface AssociationImagesUpdate {
  images: AssociationImageRequest[];
  files: File[];
}

/**
 * Build the association's `images` row set + the files to append for an
 * UPDATE. The logo row goes FIRST, so a newly picked logo consumes the first
 * uploaded file and the new gallery rows the files after it. The gallery rows
 * are built exactly as for the advisor profile, all `isSmall: 0`. No logo row
 * at all means the association ends up without a logo.
 */
export const buildAssociationImagesUpdate = (
  logo: LogoSlot,
  kept: StoredMediaFile[],
  pending: PendingMediaFile[],
): AssociationImagesUpdate => {
  const gallery = buildImagesUpdate(kept, pending);
  if (!logo) {
    return gallery;
  }
  const logoRow: AssociationImageRequest = {
    location: logo.kind === "stored" ? logo.file.location : null,
    thumbnail: null,
    streaming: null,
    type: logo.kind === "stored" ? logo.file.type : "IMAGE",
    priority: 0,
    isSmall: 1,
  };
  return {
    images: [logoRow, ...gallery.images],
    files:
      logo.kind === "pending" ? [logo.file, ...gallery.files] : gallery.files,
  };
};
